import React, { useState, useEffect } from 'react';
import {
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Eye,
  Loader2,
  Search,
  Filter,
  ExternalLink,
  User,
  Calendar,
  Wallet,
  RefreshCw,
  Download
} from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import adminWithdrawalService from '../../services/adminWithdrawalService';

const WithdrawalQueue = ({ status = 'pending', onViewDetails, onRefresh }) => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    loadWithdrawals();
  }, [status, pagination.page, searchTerm]);

  const loadWithdrawals = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(status !== 'all' && { status }),
        ...(searchTerm && { search: searchTerm })
      };

      const response = await adminWithdrawalService.getWithdrawalQueue(params);

      if (response.success) {
        setWithdrawals(response.data.withdrawals);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total,
          pages: response.data.pagination.pages
        }));
      }
    } catch (err) {
      console.error('Failed to load withdrawals:', err);
      setError(err.message || 'Failed to load withdrawals');
      toast.error('Failed to load withdrawals');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const getStatusIcon = (withdrawalStatus) => {
    const iconMap = {
      pending: <Clock className="w-4 h-4 text-yellow-600" />,
      approved: <CheckCircle2 className="w-4 h-4 text-blue-600" />,
      processing: <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />,
      submitted: <CheckCircle2 className="w-4 h-4 text-blue-600" />,
      completed: <CheckCircle2 className="w-4 h-4 text-green-600" />,
      rejected: <XCircle className="w-4 h-4 text-red-600" />,
      failed: <AlertTriangle className="w-4 h-4 text-red-600" />
    };
    return iconMap[withdrawalStatus] || <Clock className="w-4 h-4 text-gray-600" />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && withdrawals.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Loading withdrawals...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={loadWithdrawals}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by project title, owner, or wallet address..."
            value={searchTerm}
            onChange={handleSearch}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <button
          onClick={loadWithdrawals}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 
                     bg-white border border-gray-300 rounded-lg hover:bg-gray-50 
                     disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Withdrawals List */}
      {withdrawals.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Download className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {status === 'pending' ? 'No Pending Withdrawals' : 'No Withdrawals Found'}
          </h3>
          <p className="text-gray-600">
            {status === 'pending'
              ? 'All withdrawal requests have been processed.'
              : 'No withdrawal requests match your criteria.'
            }
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {withdrawals.map((withdrawal, index) => {
            const statusInfo = adminWithdrawalService.formatStatus(withdrawal.status, withdrawal.currency);
            const priorityInfo = adminWithdrawalService.getPriority(withdrawal);

            return (
              <motion.div
                key={withdrawal.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Header with Project and Owner */}
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">
                          {withdrawal.project?.title || 'Unknown Project'}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <User className="w-4 h-4" />
                          <span>{withdrawal.owner?.full_name || withdrawal.owner?.username || 'Unknown Owner'}</span>
                        </div>
                      </div>

                      {/* Priority Badge */}
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${priorityInfo.class}`}>
                        {priorityInfo.label}
                      </span>
                    </div>

                    {/* Amount and Status */}
                    <div className="flex items-center gap-4 mb-3">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(withdrawal.status)}
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${statusInfo.bgClass}`}>
                          {statusInfo.label}
                        </span>
                      </div>

                      <div className="text-xl font-bold text-gray-900">
                        {adminWithdrawalService.formatAmount(withdrawal.requested_amount, withdrawal.currency)}
                      </div>

                      {withdrawal.auto_approved && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Auto-approved
                        </span>
                      )}
                    </div>

                    {/* Details Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm text-gray-600 mb-3">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>Requested: {formatDate(withdrawal.requested_at)}</span>
                      </div>

                      <div className="flex items-center gap-2">
                        {withdrawal.currency === 'NGN' ? <RefreshCw className="w-4 h-4" /> : <Wallet className="w-4 h-4" />}
                        <span className="font-mono text-xs">
                          {withdrawal.currency === 'NGN'
                            ? `${withdrawal.bank_name} (${withdrawal.account_number})`
                            : `${withdrawal.recipient_wallet?.slice(0, 8)}...${withdrawal.recipient_wallet?.slice(-6)}`
                          }
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="font-medium">Category:</span>
                        <span>{withdrawal.project?.category || 'N/A'}</span>
                      </div>
                    </div>

                    {/* Request Reason */}
                    {withdrawal.request_reason && (
                      <div className="mb-3 p-2 bg-gray-50 rounded border-l-4 border-blue-200">
                        <p className="text-sm text-gray-700">
                          <span className="font-medium">Reason:</span> {withdrawal.request_reason}
                        </p>
                      </div>
                    )}

                    {/* Rejection Reason */}
                    {withdrawal.status === 'rejected' && withdrawal.rejection_reason && (
                      <div className="mb-3 p-2 bg-red-50 rounded border-l-4 border-red-200">
                        <p className="text-sm text-red-800">
                          <span className="font-medium">Rejection reason:</span> {withdrawal.rejection_reason}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="ml-4 flex flex-col gap-2">
                    <button
                      onClick={() => onViewDetails(withdrawal)}
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 
                                 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                      Review
                    </button>

                    {withdrawal.transaction_hash && (
                      withdrawal.currency === 'PI' ? (
                        <a
                          href={`https://pi-blockchain.net/tx/${withdrawal.transaction_hash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 
                                     bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <ExternalLink className="w-4 h-4" />
                          View Tx
                        </a>
                      ) : (
                        <div className="text-xs text-center p-2 bg-gray-50 rounded border border-gray-200 text-gray-500">
                          Flutterwave Ref Verified
                        </div>
                      )
                    )}
                  </div>
                </div>

                {/* Time Estimate for Pending */}
                {withdrawal.status === 'pending' && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500">
                      Requested {adminWithdrawalService.getTimeAgo(withdrawal.requested_at)} •
                      {withdrawal.currency === 'PI'
                        ? (withdrawal.requested_amount <= 10000
                          ? ' Would be auto-approved if ≤10,000 π'
                          : ' Requires manual approval (>10,000 π)')
                        : ' Requires manual admin review for NGN'
                      }
                    </p>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
            {pagination.total} withdrawals
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="px-3 py-2 border border-gray-300 rounded text-sm disabled:opacity-50 
                         disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              Previous
            </button>
            <span className="px-3 py-2 text-sm text-gray-600">
              Page {pagination.page} of {pagination.pages}
            </span>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.pages}
              className="px-3 py-2 border border-gray-300 rounded text-sm disabled:opacity-50 
                         disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WithdrawalQueue;
